import React from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Divider,
  IconButton,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  LocationOn,
  Phone,
  Email,
  WhatsApp,
  Home as HomeIcon,
  Facebook,
  Instagram,
  Agriculture,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import vidhiLogo from '../../assets/vidhi.png';
import { surface, brand, ink, line, status } from '../../theme/tokens';
import Seo, { farmJsonLd } from '../../components/Common/Seo';

// Cards share one shape so the row reads as a single unit.
const contactCardSx = {
  height: '100%',
  textAlign: 'center',
  border: `1px solid ${line.subtle}`,
  borderTop: `3px solid ${brand.mustard}`,
};

// Circular mustard-tinted badge behind each contact icon.
const iconBadgeSx = {
  width: 64,
  height: 64,
  mx: 'auto',
  mb: 2.5,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '50%',
  backgroundColor: 'rgba(216, 181, 16, 0.16)',
};

const ContactPage = () => {
  const navigate = useNavigate();

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
      <Seo
        title="Contact & Visit Vidhi Farm, Birbhum"
        description="Call or WhatsApp +91 85830 79287 to order produce, ask about the weekend farm stay, or find your way to our organic farm in Birbhum, West Bengal."
        path="/contact"
        jsonLd={farmJsonLd()}
      />

      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          color="inherit"
          href="/"
          onClick={(e) => {
            e.preventDefault();
            navigate('/');
          }}
          sx={{ display: 'flex', alignItems: 'center', color: ink.secondary }}
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          Home
        </Link>
        <Typography color="text.primary">Contact Us</Typography>
      </Breadcrumbs>

      {/* Page Header */}
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        {/* Large Vidhi Farm Logo */}
        <Box sx={{ mb: 4 }}>
          <Box
            component="img"
            src={vidhiLogo}
            alt="Vidhi Farm Logo"
            sx={{
              height: { xs: 100, sm: 140, md: 160 },
              width: 'auto',
              objectFit: 'contain',
              filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.1))',
              transition: 'transform 0.3s ease',
              '&:hover': {
                transform: 'scale(1.05)'
              }
            }}
          />
        </Box>
        
        <Typography
          variant="h3"
          component="h1"
          gutterBottom
          sx={{ color: ink.primary, fontWeight: 700, letterSpacing: '-0.015em' }}
        >
          Contact Vidhi Farm
        </Typography>
        <Box
          sx={{
            width: 64,
            height: 3,
            mx: 'auto',
            mb: 3,
            borderRadius: 2,
            backgroundColor: brand.mustard,
          }}
        />
        <Typography
          variant="h6"
          sx={{ color: ink.secondary, maxWidth: 640, mx: 'auto', fontWeight: 400, lineHeight: 1.7 }}
        >
          Get in touch with us for fresh organic produce, farm experiences, or any questions about our sustainable farming practices.
        </Typography>
      </Box>

      {/* Contact Information */}
      <Grid container spacing={4} justifyContent="center">
        <Grid item xs={12} md={8}>
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h5"
              component="h2"
              gutterBottom
              sx={{
                color: ink.primary,
                fontWeight: 700,
                textAlign: 'center',
                mb: 4,
                fontSize: '1.125rem',
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
              }}
            >
              Get In Touch
            </Typography>

            {/* Contact Cards */}
            <Grid container spacing={3}>
              {/* Address */}
              <Grid item xs={12} md={4}>
                <Card elevation={0} sx={contactCardSx}>
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={iconBadgeSx}>
                      <LocationOn sx={{ fontSize: 32, color: brand.brown }} />
                    </Box>
                    <Typography variant="h6" sx={{ color: ink.primary, fontWeight: 700, mb: 1 }}>
                      Visit Our Farm
                    </Typography>
                    <Typography variant="body1" sx={{ color: ink.secondary }}>
                      Vidhi Farm, Birbhum
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* Phone */}
              <Grid item xs={12} md={4}>
                <Card elevation={0} sx={contactCardSx}>
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={iconBadgeSx}>
                      <Phone sx={{ fontSize: 32, color: brand.brown }} />
                    </Box>
                    <Typography variant="h6" sx={{ color: ink.primary, fontWeight: 700, mb: 1 }}>
                      Call Us
                    </Typography>
                    <Typography variant="body1" sx={{ color: ink.secondary, mb: 2.5 }}>
                      +91 85830 79287
                    </Typography>
                    <Button
                      variant="contained"
                      href="tel:+918583079287"
                      startIcon={<Phone />}
                      size="small"
                    >
                      Call Now
                    </Button>
                  </CardContent>
                </Card>
              </Grid>

              {/* Email */}
              <Grid item xs={12} md={4}>
                <Card elevation={0} sx={contactCardSx}>
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={iconBadgeSx}>
                      <Email sx={{ fontSize: 32, color: brand.brown }} />
                    </Box>
                    <Typography variant="h6" sx={{ color: ink.primary, fontWeight: 700, mb: 1 }}>
                      Email Us
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ color: ink.secondary, mb: 2.5, wordBreak: 'break-word' }}
                    >
                      ananya0066@gmail.com
                    </Typography>
                    <Button
                      variant="contained"
                      href="mailto:ananya0066@gmail.com"
                      startIcon={<Email />}
                      size="small"
                    >
                      Email Us
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>

          {/* WhatsApp Section */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Button
              variant="outlined"
              href="https://wa.me/918583079287?text=Hello%20Vidhi%20Farm!%20I%20am%20interested%20in%20your%20organic%20products.%20Please%20share%20more%20details."
              target="_blank"
              startIcon={<WhatsApp />}
              size="large"
              sx={{ 
                // WhatsApp green (#25D366) is 1.75:1 on the page -- a surface
                // colour, not a label colour. Use the checked green ink (5.54:1).
                color: status.success,
                borderColor: status.success,
                fontWeight: 600,
                px: 4,
                '&:hover': {
                  backgroundColor: 'rgba(61, 107, 51, 0.08)',
                  borderColor: status.success
                }
              }}
            >
              WhatsApp Us
            </Button>
          </Box>

          {/* Social Media */}
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="h6"
              component="h2"
              gutterBottom
              sx={{
                color: ink.primary,
                fontWeight: 700,
                fontSize: '0.9375rem',
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                mb: 2,
              }}
            >
              Follow Us
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
              <IconButton
                href="https://www.facebook.com/share/17MbFMasdY/?mibextid=wwXIfr"
                target="_blank"
                sx={{ 
                  color: brand.brown,
                  border: `1px solid ${line.default}`,
                  // icon-only hover tint: brand colours are graphics here, not text
                  '&:hover': { 
                    color: '#4267B2',
                    borderColor: '#4267B2',
                    backgroundColor: 'rgba(66, 103, 178, 0.08)'
                  }
                }}
              >
                <Facebook fontSize="large" />
              </IconButton>
              <IconButton
                href="https://www.instagram.com/vidhi_permaculture_farm?igsh=MWJiYWtzbjUwOW1lbQ=="
                target="_blank"
                sx={{ 
                  color: brand.brown,
                  border: `1px solid ${line.default}`,
                  '&:hover': { 
                    color: '#C13584',
                    borderColor: '#C13584',
                    backgroundColor: 'rgba(193, 53, 132, 0.08)'
                  }
                }}
              >
                <Instagram fontSize="large" />
              </IconButton>
            </Box>
          </Box>
        </Grid>
      </Grid>

      <Divider sx={{ my: 6, borderColor: line.default }} />

      {/* Farm Information */}
      <Box
        sx={{
          textAlign: 'center',
          backgroundColor: surface.sunken,
          borderRadius: 3,
          px: { xs: 3, md: 6 },
          py: { xs: 4, md: 5 },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2 }}>
          <Agriculture sx={{ fontSize: 40, mr: 2, color: brand.brown }} />
          <Typography variant="h4" sx={{ color: ink.primary, fontWeight: 700 }}>
            Visit Vidhi Farm
          </Typography>
        </Box>
        <Typography
          variant="body1"
          sx={{ color: ink.secondary, maxWidth: 760, mx: 'auto', lineHeight: 1.8 }}
        >
          Experience sustainable farming firsthand at our organic farm in Birbhum. 
          We welcome visitors to learn about our farming practices, enjoy fresh produce, 
          and connect with nature. Contact us to schedule a visit or farm tour.
        </Typography>
      </Box>
    </Container>
  );
};

export default ContactPage;